<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import { useReportDrilldown } from 'dashboard/routes/dashboard/settings/reports/composables/useReportDrilldown';
import ReportDrilldownCard from 'dashboard/routes/dashboard/settings/reports/components/ReportDrilldownCard.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SidePanel from 'dashboard/components-next/side-panel/SidePanel.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, default: '' },
  attributeKey: { type: String, default: '' },
  value: { type: String, default: '' },
  parentAttributeKey: { type: String, default: '' },
  parentValue: { type: String, default: '' },
  from: { type: [String, Number], default: '' },
  to: { type: [String, Number], default: '' },
});

const emit = defineEmits(['close']);

const panelRef = ref(null);
const { t } = useI18n();
const {
  records,
  meta,
  isFetching,
  isFetchingMore,
  hasError,
  hasRecords,
  hasMore,
  open: openDrilldown,
  close,
  loadMore,
} = useReportDrilldown(params =>
  ReportsAPI.getCustomAttributeDrilldown(params)
);

const subtitle = computed(() => {
  if (!meta.value.total_count) return '';

  return t(
    'CUSTOM_ATTRIBUTE_ANALYTICS_REPORTS.DRILLDOWN.RESULT_COUNT_CONVERSATION',
    { count: meta.value.total_count }
  );
});

const recordKey = record => `${record.conversation?.id}-${record.occurred_at}`;

const fetchDrilldown = () => {
  if (!props.attributeKey || !props.value) return;

  openDrilldown({
    attributeKey: props.attributeKey,
    value: props.value,
    parentAttributeKey: props.parentAttributeKey,
    parentValue: props.parentValue,
    from: props.from,
    to: props.to,
  });
};

watch(
  () => props.open,
  isDrawerOpen => {
    if (!isDrawerOpen) {
      panelRef.value?.close();
      close();
      return;
    }

    panelRef.value?.open();
    fetchDrilldown();
  }
);

watch(
  () => [
    props.attributeKey,
    props.value,
    props.parentAttributeKey,
    props.parentValue,
  ],
  () => {
    if (props.open) fetchDrilldown();
  }
);
</script>

<template>
  <SidePanel ref="panelRef" :title="title" width="xl" @close="emit('close')">
    <template #header>
      <div class="min-w-0">
        <h3 class="truncate text-base font-medium text-n-slate-12">
          {{ title }}
        </h3>
        <div class="mt-1 text-sm text-n-slate-11">
          {{ subtitle }}
        </div>
      </div>
    </template>
    <div v-if="isFetching" class="flex h-40 items-center justify-center">
      <Spinner />
    </div>

    <div
      v-else-if="hasError"
      class="flex h-40 items-center justify-center text-sm text-n-ruby-11"
    >
      {{ $t('CUSTOM_ATTRIBUTE_ANALYTICS_REPORTS.DRILLDOWN.ERROR') }}
    </div>

    <div
      v-else-if="!hasRecords"
      class="flex h-40 items-center justify-center text-sm text-n-slate-10"
    >
      {{ $t('CUSTOM_ATTRIBUTE_ANALYTICS_REPORTS.DRILLDOWN.EMPTY') }}
    </div>

    <div v-else class="flex flex-col gap-2">
      <ReportDrilldownCard
        v-for="record in records"
        :key="recordKey(record)"
        :record="record"
      />

      <Button
        v-if="hasMore"
        faded
        slate
        size="sm"
        class="mx-auto mt-2"
        :label="$t('CUSTOM_ATTRIBUTE_ANALYTICS_REPORTS.DRILLDOWN.LOAD_MORE')"
        :is-loading="isFetchingMore"
        @click="loadMore"
      />
    </div>
  </SidePanel>
</template>
